import { BaseService } from '../base';
import type {
  CreateProductPaymentMethodPayload,
  ProductPaymentSettings,
  ProductPaymentSettingsList,
  UpdateProductPaymentGatewayPayload,
  UpdateProductPaymentMethodPayload,
} from './types';

export type * from './types';

export type PaymentProvider = 'LEGACY' | 'FINANCE_API';
export type FinancePaymentMethod = 'BOLETO_PIX' | 'PIX' | 'CREDIT_CARD';
/** Como o PIX parcelado é cobrado: link por parcela ou PIX Automático (mandato). */
export type PixBillingMode = 'SCHEDULED' | 'AUTOMATIC';
/**
 * Como o cartão parcelado é cobrado: um link à vista por parcela, ou a apólice em
 * parcela única e um link só, parcelado no cartão.
 */
export type CardBillingMode = 'PER_INSTALLMENT' | 'SINGLE_TRANSACTION';

export type ProductPaymentConfig = {
  productCode: string;
  paymentProvider: PaymentProvider;
  enabledMethods: FinancePaymentMethod[];
  /**
   * Quando true, a proposta aceita UMA única forma de pagamento — o link é o
   * canal de cobrança, então "meio diferente nas demais parcelas" não se aplica.
   */
  singlePaymentMethodOnly: boolean;
  /**
   * CodFormas do catálogo que o combo deve oferecer: as do gateway ligadas e
   * as legadas. Lista VAZIA significa "não filtre" — é o caso de todo produto
   * no fluxo tradicional.
   */
  allowedPaymentMethodCodes: string[];
  /**
   * Quais delas são cobradas por link de pagamento. As demais são legadas: a
   * apólice segue a cobrança do I4Pro. Vazia no fluxo tradicional.
   */
  gatewayPaymentMethodCodes: string[];
  /**
   * CodFormas que saem do combo, com ou sem gateway: formas do gateway que o
   * link não cobra agora (desligadas no produto, ou o gateway inteiro).
   * Opcional para tolerar a API anterior durante o deploy.
   */
  hiddenPaymentMethodCodes?: string[];
  /**
   * Só em FINANCE_API: quantos dias antes de cada vencimento o link da parcela é
   * gerado e enviado ao segurado.
   */
  chargeLeadDays?: number;
  /**
   * Só em FINANCE_API: `AUTOMATIC` = o PIX parcelado vira PIX Automático, e o
   * step de Pagamento pede a conta do segurado.
   */
  pixBillingMode?: PixBillingMode;
  /**
   * Só em FINANCE_API: `SINGLE_TRANSACTION` = no cartão parcelado a apólice é
   * emitida em parcela única e o segurado parcela no próprio cartão.
   */
  cardBillingMode?: CardBillingMode;
};

export class ProductPaymentConfigService extends BaseService {
  /**
   * Config de cobrança do produto. Nunca 404: produto sem configuração devolve
   * `LEGACY`, que é o fluxo InsureMO/I4Pro de sempre.
   */
  async getPaymentConfig(productCode: string): Promise<{
    success: boolean;
    data?: ProductPaymentConfig;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/products/${encodeURIComponent(productCode)}/payment-config`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);

      return await this.handleJsonResponse<ProductPaymentConfig>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar a configuração de pagamento do produto',
      };
    }
  }

  /**
   * Admin seguradora — Configurações > Produtos > Formas de pagamento. Todos os
   * produtos, com o status da cobrança e do catálogo.
   */
  async listPaymentSettings(): Promise<{
    success: boolean;
    data?: ProductPaymentSettingsList;
    error?: string;
  }> {
    try {
      const response = await this.get(
        '/portal/v1/products/payment-settings',
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<ProductPaymentSettingsList>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar as formas de pagamento dos produtos',
      };
    }
  }

  /** Cobrança pelo gateway e catálogo de formas de um produto. */
  async getPaymentSettings(productCode: string): Promise<{
    success: boolean;
    data?: ProductPaymentSettings;
    error?: string;
  }> {
    try {
      const response = await this.get(
        `${settingsPath(productCode)}`,
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<ProductPaymentSettings>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar as formas de pagamento do produto',
      };
    }
  }

  /** Salva a aba "Cobrança e formas". 409 quando outra pessoa salvou antes. */
  async updatePaymentGateway(
    productCode: string,
    payload: UpdateProductPaymentGatewayPayload
  ): Promise<{
    success: boolean;
    data?: ProductPaymentSettings;
    error?: string;
  }> {
    try {
      const response = await this.put(
        `${settingsPath(productCode)}/gateway`,
        payload,
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<ProductPaymentSettings>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao salvar a cobrança do produto',
      };
    }
  }

  /** Adiciona uma forma ao catálogo do produto. */
  async createPaymentMethod(
    productCode: string,
    payload: CreateProductPaymentMethodPayload
  ): Promise<{
    success: boolean;
    data?: ProductPaymentSettings;
    error?: string;
  }> {
    try {
      const response = await this.post(
        `${settingsPath(productCode)}/methods`,
        payload,
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<ProductPaymentSettings>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao adicionar a forma de pagamento',
      };
    }
  }

  /** Edita, desativa ou reativa uma forma do catálogo. */
  async updatePaymentMethod(
    productCode: string,
    methodCode: string,
    payload: UpdateProductPaymentMethodPayload
  ): Promise<{
    success: boolean;
    data?: ProductPaymentSettings;
    error?: string;
  }> {
    try {
      const response = await this.put(
        `${settingsPath(productCode)}/methods/${encodeURIComponent(methodCode)}`,
        payload,
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<ProductPaymentSettings>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao salvar a forma de pagamento',
      };
    }
  }
}

function settingsPath(productCode: string): string {
  return `/portal/v1/products/${encodeURIComponent(productCode)}/payment-settings`;
}

export const productPaymentConfigService = new ProductPaymentConfigService();
